import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Users, ChartLine, ListChecks, EnvelopeSimple, ArrowRight, Barbell, Sparkle,
  Trophy, WarningOctagon, Fire, Bed, Timer, ChatCircleDots, Target, PaperPlaneTilt,
  CalendarBlank, Heartbeat, Medal, CheckCircle, Play, Warning, Bandaids, ClipboardText,
  Table, Calculator, Gauge, Flag, MapPin, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import CoachOnboardingWizard from "@/components/CoachOnboardingWizard";
import CoachDashboardSkeleton from "@/components/coach/CoachDashboardSkeleton";
import CoachQuickStart from "@/components/coach/CoachQuickStart";
import CoachMomentum from "@/components/coach/CoachMomentum";
import CoachTour from "@/components/coach/CoachTour";
import { useAuth } from "@/contexts/AuthContext";
import { useAcademy } from "@/contexts/AcademyContext";

const CHECKIN_TEMPLATES = [
  {
    id: "friendly",
    label: "Friendly nudge",
    text: "Hey! Haven't seen you log a workout in a few days — everything good?",
  },
  {
    id: "program",
    label: "Adjust program",
    text: "Missing you this week. Want me to tweak your program so it fits your schedule?",
  },
  {
    id: "blocker",
    label: "Ask what's blocking",
    text: "Quick check-in: what's getting in the way of training this week? Let's problem-solve.",
  },
  {
    id: "encourage",
    label: "Encouragement",
    text: "Consistency beats intensity. One workout today gets us right back on track — you've got this.",
  },
];

const fmtDaysAgo = (n) => {
  if (n == null) return "NEVER";
  if (n === 0) return "TODAY";
  if (n === 1) return "1 DAY AGO";
  return `${n} DAYS AGO`;
};

const complianceColor = (pct) => {
  if (pct == null) return "text-text-secondary";
  if (pct >= 80) return "text-green-400";
  if (pct >= 40) return "text-yellow-400";
  return "text-red-400";
};

function ClientProgressCard({ c }) {
  const cid = c.client?.user_id;
  const [nudging, setNudging] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);

  const sendNudge = async (template) => {
    if (nudging) return;
    setNudging(true);
    try {
      await api.post(`/coach/clients/${cid}/messages`, { text: template.text });
      toast.success(`Check-in sent to ${c.client?.name}`);
      setNudgeOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setNudging(false);
    }
  };

  // Readiness colour-coding — quick green/amber/red risk scan across the roster.
  const rFlag = c.readiness?.flag || null;
  const rStyle = {
    green: { rail: "border-l-emerald-500", wash: "bg-emerald-50/40", dot: "bg-emerald-500", chip: "text-emerald-700 border-emerald-300 bg-emerald-50", label: "READY" },
    amber: { rail: "border-l-amber-500", wash: "bg-amber-50/40", dot: "bg-amber-500", chip: "text-amber-700 border-amber-300 bg-amber-50", label: "CAUTION" },
    red:   { rail: "border-l-red-500", wash: "bg-red-50/40", dot: "bg-red-500", chip: "text-red-700 border-red-300 bg-red-50", label: "FATIGUE" },
  }[rFlag] || { rail: "border-l-slate-300", wash: "", dot: "bg-slate-300", chip: "text-text-secondary border-slate-200", label: "NO DATA" };

  return (
    <div
      className={`border border-l-[4px] ${rStyle.rail} ${c.at_risk ? "border-red-300" : "border-slate-200"} ${rStyle.wash} rounded-md p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg`}
      data-testid={`client-progress-${cid}`}
      data-readiness-flag={rFlag || "none"}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        {c.client?.picture ? (
          <img src={c.client.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-brand-primary flex items-center justify-center text-xs font-bold">
            {c.client?.name?.[0]?.toUpperCase() || "?"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <Link
            to={`/coach/clients/${cid}`}
            className="font-heading text-sm truncate hover:text-brand-accent transition-colors block"
            data-testid={`client-progress-name-${cid}`}
          >
            {c.client?.name || "Unknown"}
          </Link>
          <div className="label-tiny text-text-secondary mt-0.5">
            {fmtDaysAgo(c.days_since_last_workout)}
          </div>
        </div>
        <span
          className={`flex items-center gap-1 label-tiny border px-2 py-0.5 rounded ${rStyle.chip}`}
          data-testid={`readiness-flag-${cid}`}
          title={c.readiness?.score != null ? `Latest readiness ${c.readiness.score}` : "No recent readiness check-in"}
        >
          <span className={`h-2 w-2 rounded-full ${rStyle.dot}`} />
          {c.readiness?.score != null ? `${c.readiness.score} · ${rStyle.label}` : rStyle.label}
        </span>
        {c.at_risk && (
          <span
            className="flex items-center gap-1 label-tiny text-red-600 border border-red-300 bg-red-50 px-2 py-0.5 rounded"
            data-testid={`at-risk-${cid}`}
          >
            <WarningOctagon size={10} weight="fill" /> AT RISK
          </span>
        )}
      </div>

      {/* Compliance bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="label-tiny">COMPLIANCE 7D</span>
          <span className={`text-xs font-bold tabular-nums ${complianceColor(c.compliance_pct)}`}>
            {c.compliance_pct ?? "—"}%
          </span>
        </div>
        <div className="h-1.5 bg-white/10 overflow-hidden">
          <div
            className={`h-full transition-all ${
              c.compliance_pct >= 80
                ? "bg-green-400"
                : c.compliance_pct >= 40
                ? "bg-yellow-400"
                : "bg-red-400"
            }`}
            style={{ width: `${Math.max(3, Math.min(100, c.compliance_pct || 0))}%` }}
          />
        </div>
        <div className="label-tiny text-text-secondary mt-1">
          {c.workouts_7d} / {c.target_sessions_per_week} SESSIONS
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <div className="border border-slate-100 py-2">
          <Barbell size={14} weight="duotone" className="text-brand-accent mx-auto mb-0.5" />
          <div className="text-sm font-heading tabular-nums" data-testid={`workouts-${cid}`}>
            {c.workouts_7d}
          </div>
          <div className="label-tiny text-[9px]">WORKOUTS</div>
        </div>
        <div className="border border-slate-100 py-2">
          <Trophy size={14} weight="duotone" className="text-brand-accent mx-auto mb-0.5" />
          <div className="text-sm font-heading tabular-nums" data-testid={`prs-${cid}`}>
            {c.prs_7d}
          </div>
          <div className="label-tiny text-[9px]">NEW PRS</div>
        </div>
        <div className="border border-slate-100 py-2">
          <Fire size={14} weight="duotone" className="text-brand-accent mx-auto mb-0.5" />
          <div className="text-sm font-heading tabular-nums" data-testid={`volume-${cid}`}>
            {c.volume_7d >= 1000 ? `${(c.volume_7d / 1000).toFixed(1)}k` : c.volume_7d}
          </div>
          <div className="label-tiny text-[9px]">VOLUME</div>
        </div>
      </div>

      {/* Trend & Program */}
      <div className="space-y-1.5">
        {c.active_program ? (
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 truncate">
              <Target size={12} weight="duotone" className="text-brand-accent shrink-0" />
              <span className="text-text-secondary truncate">{c.active_program.name}</span>
            </div>
            <span className="tabular-nums text-brand-accent shrink-0 ml-2">
              {c.active_program.progress_pct}%
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-text-secondary/60">
            <Target size={12} weight="duotone" />
            <span>No active program</span>
          </div>
        )}

        {(c.trend_7d?.avg_sleep_hours != null || c.trend_7d?.avg_recovery_score != null) && (
          <div className="flex items-center gap-3 text-xs text-text-secondary">
            {c.trend_7d.avg_sleep_hours != null && (
              <span className="flex items-center gap-1">
                <Bed size={11} weight="duotone" />
                <span className="tabular-nums">{c.trend_7d.avg_sleep_hours}h</span>
              </span>
            )}
            {c.trend_7d.avg_recovery_score != null && (
              <span className="flex items-center gap-1">
                <Timer size={11} weight="duotone" />
                <span className="tabular-nums">{c.trend_7d.avg_recovery_score}</span>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
        <Link
          to={`/coach/clients/${cid}`}
          className="flex-1 text-center label-tiny py-1.5 border border-slate-200 hover:border-brand-accent hover:text-brand-accent transition-colors"
          data-testid={`open-client-${cid}`}
        >
          OPEN
        </Link>
        {c.at_risk ? (
          <Popover open={nudgeOpen} onOpenChange={setNudgeOpen}>
            <PopoverTrigger asChild>
              <button
                className="flex-1 flex items-center justify-center gap-1 label-tiny py-1.5 bg-brand-accent text-surface-base hover:brightness-110 transition-all disabled:opacity-50"
                data-testid={`nudge-client-${cid}`}
                disabled={nudging}
                aria-label="Send check-in"
              >
                <PaperPlaneTilt size={11} weight="bold" />
                {nudging ? "SENDING..." : "SEND CHECK-IN"}
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-72 bg-surface-DEFAULT border-slate-200 text-white p-0"
              data-testid={`nudge-popover-${cid}`}
              align="end"
            >
              <div className="px-3 py-2 border-b border-slate-200 label-tiny text-brand-accent">
                // PICK A CHECK-IN
              </div>
              <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {CHECKIN_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => sendNudge(tpl)}
                    disabled={nudging}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-100 transition-colors disabled:opacity-50"
                    data-testid={`nudge-template-${tpl.id}-${cid}`}
                  >
                    <div className="label-tiny text-brand-accent mb-1">{tpl.label.toUpperCase()}</div>
                    <div className="text-xs text-text-secondary line-clamp-2 leading-snug">
                      {tpl.text}
                    </div>
                  </button>
                ))}
              </div>
              <Link
                to={`/coach/clients/${cid}`}
                onClick={() => setNudgeOpen(false)}
                className="block px-3 py-2 border-t border-slate-200 label-tiny text-text-secondary hover:text-white hover:bg-slate-100 transition-colors text-center"
                data-testid={`nudge-custom-${cid}`}
              >
                WRITE CUSTOM MESSAGE →
              </Link>
            </PopoverContent>
          </Popover>
        ) : (
          <Link
            to={`/coach/clients/${cid}`}
            className="flex-1 flex items-center justify-center gap-1 label-tiny py-1.5 border border-slate-200 hover:border-brand-accent hover:text-brand-accent transition-colors"
            data-testid={`message-client-${cid}`}
          >
            <ChatCircleDots size={11} /> MESSAGE
          </Link>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Peaking Now — every athlete in the ≤7 day meet window
// ─────────────────────────────────────────────────────────────
function PeakWeekPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/coach/peak-week/roster");
      setData(data);
    } catch (e) { /* silent — panel just hides */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshAlerts = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/coach/peak-week/run");
      if (data?.emitted) {
        toast.success(`${data.emitted} new peak-week alert${data.emitted === 1 ? "" : "s"} sent`);
      } else {
        toast.success("All peak-week alerts already up to date");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setRefreshing(false); }
  };

  if (loading) return null;
  const items = data?.items || [];
  const count = items.length;

  return (
    <div className="tactical-card p-5 mb-8" data-testid="peak-week-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="label-tiny text-brand-accent mb-0.5 flex items-center gap-1.5">
            <Flag size={12} weight="fill" className="text-red-400" /> // PEAKING NOW
          </div>
          <div className="font-heading text-2xl">
            {count === 0
              ? "NO ATHLETES IN PEAK WEEK"
              : `${count} ATHLETE${count === 1 ? "" : "S"} INSIDE THE 7-DAY WINDOW`}
          </div>
          <div className="text-xs text-text-secondary mt-1">
            Lock the taper. Every athlete here has a meet within a week.
          </div>
        </div>
        <button
          onClick={refreshAlerts}
          disabled={refreshing}
          className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-3 py-1.5 text-xs label-tiny tracking-widest transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="peak-week-refresh-btn"
        >
          <ArrowsClockwise size={12} weight="bold" /> {refreshing ? "SYNCING..." : "SYNC ALERTS"}
        </button>
      </div>

      {count === 0 ? (
        <div className="text-center py-6 text-text-secondary text-sm" data-testid="peak-week-empty">
          Roster is off-cycle. Alerts fire the moment any athlete&apos;s next meet lands within 7 days.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map((it) => {
            const dl = it.days_left ?? 0;
            const style =
              dl <= 2 ? "border-red-500/50 bg-red-500/[0.06] text-red-400"
              : dl <= 5 ? "border-amber-500/50 bg-amber-500/[0.06] text-amber-400"
              : "border-brand-accent/40 bg-brand-accent/[0.06] text-brand-accent";
            const uid = it.athlete?.user_id;
            return (
              <Link
                key={it.meet?.meet_id || uid}
                to={`/coach/clients/${uid}`}
                className={`block border ${style} p-3 hover:brightness-125 transition`}
                data-testid={`peak-week-item-${uid}`}
              >
                <div className="flex items-center gap-2">
                  {it.athlete?.picture ? (
                    <img src={it.athlete.picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-xs font-bold shrink-0 text-white">
                      {it.athlete?.name?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-sm truncate text-white">{it.athlete?.name}</div>
                    <div className="label-tiny text-text-secondary truncate">
                      {it.meet?.name || "Meet"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-heading text-2xl tabular-nums leading-none">{dl}</div>
                    <div className="label-tiny">DAY{dl === 1 ? "" : "S"}</div>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-white/5 text-[10px] text-text-secondary flex flex-wrap gap-x-2 gap-y-0.5">
                  <span>{it.meet?.date}</span>
                  {it.meet?.discipline && <span>· {it.meet.discipline}</span>}
                  {it.meet?.location && (
                    <span className="inline-flex items-center gap-0.5"><MapPin size={9} /> {it.meet.location}</span>
                  )}
                  {it.meet?.goal && <span>· GOAL: {it.meet.goal}</span>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CoachDashboard() {
  const { user } = useAuth();
  const { academy, role: academyRole } = useAcademy();
  const isAcademyCoach = !!academy && academyRole === "coach";
  const isAcademyAdmin = !!academy && academyRole === "admin";
  const showAIBuilder = !isAcademyCoach && !isAcademyAdmin;
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [status, setStatus] = useState(null);
  const [widgets, setWidgets] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [sort, setSort] = useState("at-risk");
  const [loading, setLoading] = useState(true);
  const [showCoachOnboarding, setShowCoachOnboarding] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Fitness Trainers use this same dashboard but with a slimmer, hypertrophy-focused
  // toolkit — S&C-only tabs (Load Calculator, Autoregulation, Deload/Peak) are hidden.
  // 1RM Board stays because it doubles as a load anchor for hypertrophy programming.
  const isFitnessTrainer = user?.primary_role === "fitness_trainer";
  const tabParam = searchParams.get("tab") || "overview";
  const scOnlyTabs = ["load-calc", "autoreg", "planner"];
  const allValidTabs = ["overview", "one-rm", "load-calc", "autoreg", "volume", "templates", "planner"];
  const validTabs = isFitnessTrainer
    ? allValidTabs.filter((t) => !scOnlyTabs.includes(t))
    : allValidTabs;
  const tab = validTabs.includes(tabParam) ? tabParam : "overview";
  const setTab = (t) => setSearchParams({ tab: t }, { replace: false });

  useEffect(() => {
    Promise.all([
      api.get("/coach/dashboard").catch(() => ({ data: null })),
      api.get("/coach/dashboard/progress").catch(() => ({ data: null })),
      api.get("/pro/status"),
      api.get("/coach/dashboard/widgets").catch(() => ({ data: null })),
      api.get("/coach/alerts?within_days=7").catch(() => ({ data: null })),
    ]).then(([{ data: dash }, { data: prog }, { data: st }, { data: w }, { data: al }]) => {
      setData(dash);
      setProgress(prog);
      setStatus(st);
      setWidgets(w);
      setAlerts(al);
      setLoading(false);
      if (!st.is_coach_pro) navigate("/upgrade?tab=coach");
    });
  }, [navigate]);

  useEffect(() => {
    if (user && user.is_coach && !user.coach_onboarded_at) {
      setShowCoachOnboarding(true);
    }
  }, [user]);

  if (loading) return <CoachDashboardSkeleton />;
  if (!data || !status?.is_coach_pro) return null;

  const summary = progress?.roster_summary || {
    total_clients: data.total_clients,
    active_clients_7d: data.active_clients_7d,
    at_risk_clients_7d: data.inactive_clients_7d,
    workouts_7d: 0,
    prs_7d: 0,
  };

  const sortedClients = [...(progress?.clients || [])].sort((a, b) => {
    if (sort === "at-risk") {
      if (a.at_risk !== b.at_risk) return a.at_risk ? -1 : 1;
      return (a.days_since_last_workout ?? 999) - (b.days_since_last_workout ?? 999);
    }
    if (sort === "compliance") return (a.compliance_pct ?? 0) - (b.compliance_pct ?? 0);
    if (sort === "name") return (a.client?.name || "").localeCompare(b.client?.name || "");
    return 0;
  });

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="coach-dashboard-page">
      <CoachOnboardingWizard
        open={showCoachOnboarding}
        onComplete={() => setShowCoachOnboarding(false)}
      />
      <CoachTour
        enabled={
          tab === "overview" &&
          !!user?.is_coach &&
          !!user?.coach_onboarded_at &&
          !user?.onboarding_dismissed?.coach_tour &&
          !showCoachOnboarding
        }
      />
      <div className="mb-8">
        <div className="label-tiny text-brand-accent mb-2">// COACH MODE</div>
        <h1 className="font-heading text-4xl sm:text-5xl">COACH DASHBOARD</h1>
        <p className="text-text-secondary text-sm mt-2">
          Roster-wide progress. Every client at a glance.
        </p>
      </div>

      <div className="border-b border-white/10 flex gap-1 overflow-x-auto mb-8" data-testid="coach-tabs">
        {[
          { id: "overview",  label: "Overview",         icon: ChartLine },
          { id: "one-rm",    label: "1RM Board",        icon: Table },
          { id: "load-calc", label: "Load Calculator",  icon: Calculator, scOnly: true },
          { id: "autoreg",   label: "Autoregulation",   icon: Gauge,      scOnly: true },
          { id: "volume",    label: "Volume Report",    icon: ChartLine },
          { id: "templates", label: "Templates",        icon: ListChecks },
          { id: "planner",   label: "Deload/Peak",      icon: Fire,       scOnly: true },
        ].filter((t) => !(isFitnessTrainer && t.scOnly)).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`coach-tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm label-tiny tracking-wider transition border-b-2 -mb-px ${
              tab === t.id
                ? "border-heat text-heat"
                : "border-transparent text-text-secondary hover:text-heat"
            }`}
          >
            <t.icon size={14} weight="duotone" /> {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "one-rm"    && <OneRepMaxBoard />}
      {tab === "load-calc" && <LoadCalculatorTab />}
      {tab === "autoreg"   && <AutoregTab clients={progress?.clients || []} />}
      {tab === "volume"    && <VolumeReportTab clients={progress?.clients || []} />}
      {tab === "templates" && <TemplatesTab />}
      {tab === "planner"   && <PlannerTab />}

      {tab === "overview" && <>

      {/* First-run guidance + engagement for coaches */}
      {!isAcademyCoach && !isAcademyAdmin && <CoachQuickStart data={data} />}
      <CoachMomentum />

      {/* Live: who's training right now */}
      <TrainingNowStrip />

      {/* Coach hub widgets — Today's Sessions & Needs Attention first */}
      {widgets && (
        <WidgetsSection widgets={widgets} alerts={alerts} />
      )}

      {/* Roster summary (top KPIs) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {[
          {
            label: "ACTIVE CLIENTS",
            val: `${summary.active_clients_7d} / ${summary.total_clients}`,
            testid: "metric-active",
            icon: Users,
            hint: "logged this week",
            tint: "kpi-cyan",
          },
          {
            label: "WORKOUTS 7D",
            val: summary.workouts_7d,
            testid: "metric-workouts",
            icon: Barbell,
            hint: "across roster",
            tint: "kpi-blue",
          },
          {
            label: "NEW PRS 7D",
            val: summary.prs_7d,
            testid: "metric-prs",
            icon: Trophy,
            hint: "this week",
            tint: "kpi-amber",
          },
          {
            label: "AT RISK",
            val: summary.at_risk_clients_7d,
            testid: "metric-at-risk",
            icon: WarningOctagon,
            hint: "inactive 7+ days",
            danger: summary.at_risk_clients_7d > 0,
            tint: summary.at_risk_clients_7d > 0 ? "kpi-red" : "kpi-emerald",
          },
        ].map((m) => (
          <div key={m.label} className={`kpi-tile ${m.tint}`} data-testid={m.testid}>
            <div className="flex items-center justify-between mb-3">
              <span className="kpi-pill">
                <m.icon size={18} weight="duotone" />
              </span>
              <span className="label-tiny">{m.label}</span>
            </div>
            <div className="kpi-num font-heading text-3xl tabular-nums">
              {m.val}
            </div>
            <div className="label-tiny text-text-secondary mt-1 text-[10px]">{m.hint}</div>
          </div>
        ))}
      </div>

      {/* Peaking Now panel — every athlete in the ≤7 day meet window.
          Hidden for Fitness Trainers (competition-focused, not relevant to gen-pop clients). */}
      {!isFitnessTrainer && <PeakWeekPanel />}
      {/* Quick actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {[
          { to: "/coach/clients", label: isAcademyCoach ? "MY ATHLETES" : "MANAGE CLIENTS", desc: `Roster · ${data.total_clients} / ${data.client_limit}`, testid: "quick-clients" },
          ...(isAcademyCoach || isAcademyAdmin ? [] : [
            { to: "/coach/programs", label: "PROGRAM LIBRARY", desc: `${data.total_programs} programs · ${data.active_assignments} assigned`, testid: "quick-programs" },
          ]),
          ...(showAIBuilder ? [
            { to: "/ai-coach", label: "AI PROGRAM BUILDER", desc: "Generate programs to assign to clients", testid: "quick-ai-program-builder" },
          ] : []),
        ].map((q) => (
          <Link to={q.to} key={q.to} data-testid={q.testid}>
            <div className="tactical-card p-6 hover:bg-surface-elevated transition-colors group cursor-pointer">
              <div className="flex items-center justify-between mb-3">
                <span className="label-tiny text-brand-accent">// GO</span>
                <ArrowRight
                  size={16}
                  className="text-text-secondary group-hover:text-brand-accent group-hover:translate-x-1 transition-all"
                />
              </div>
              <div className="font-heading text-xl mb-1">{q.label}</div>
              <div className="text-xs text-text-secondary">{q.desc}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* Client progress */}
      <div className="mb-8" data-testid="client-progress-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-tiny text-brand-accent mb-1">// CLIENT PROGRESS</div>
            <h2 className="font-heading text-2xl">ROSTER — LAST 7 DAYS</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="label-tiny">SORT</span>
            {[
              { k: "at-risk", label: "AT RISK" },
              { k: "compliance", label: "COMPLIANCE" },
              { k: "name", label: "NAME" },
            ].map((opt) => (
              <button
                key={opt.k}
                onClick={() => setSort(opt.k)}
                className={`label-tiny px-2 py-1 border transition-colors ${
                  sort === opt.k
                    ? "border-brand-accent text-brand-accent"
                    : "border-slate-200 text-text-secondary hover:text-white"
                }`}
                data-testid={`sort-${opt.k}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {sortedClients.length === 0 ? (
          <div className="tactical-card p-10 text-center" data-testid="progress-empty">
            <Users size={40} weight="duotone" className="text-brand-accent mx-auto mb-3" />
            <p className="text-sm text-text-secondary">
              No clients yet. Invite your first athlete to see roster progress.
            </p>
            <Link
              to="/coach/clients"
              className="btn-kinetic inline-block mt-4 px-4 py-2 text-xs label-tiny"
              data-testid="empty-invite-cta"
            >
              INVITE CLIENT
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sortedClients.map((c) => (
              <ClientProgressCard key={c.link_id} c={c} />
            ))}
          </div>
        )}
      </div>

      {/* Recent activity */}
      <div className="tactical-card p-6" data-testid="recent-activity">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg">// RECENT CLIENT ACTIVITY</h3>
          <span className="label-tiny">LAST 7 DAYS</span>
        </div>
        {data.recent_activity.length === 0 ? (
          <div className="text-center py-10">
            <Barbell size={36} weight="duotone" className="text-text-secondary opacity-40 mx-auto mb-3" />
            <p className="text-sm text-text-secondary">
              No client workouts this week. Time to check in.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.recent_activity.map((w) => (
              <div
                key={w.log_id}
                className="flex items-center justify-between border border-slate-100 p-3 hover:bg-surface-elevated transition-colors"
              >
                <div>
                  <div className="text-sm">
                    <span className="font-heading">{w.client_name?.toUpperCase()}</span>
                    <span className="text-text-secondary"> logged </span>
                    <span className="text-brand-accent">{w.name}</span>
                  </div>
                  <div className="label-tiny mt-1">
                    {w.category} · {w.duration_min} MIN · {new Date(w.date).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {summary.at_risk_clients_7d > 0 && (
        <div
          className="mt-4 tactical-card p-4 border-red-400/30"
          data-testid="inactive-warning"
        >
          <div className="flex items-center gap-3">
            <Sparkle size={20} weight="fill" className="text-red-400" />
            <div>
              <div className="text-sm">
                {summary.at_risk_clients_7d} client{summary.at_risk_clients_7d > 1 ? "s" : ""}{" "}
                haven&apos;t logged this week
              </div>
              <div className="label-tiny mt-1">
                Send a check-in message or adjust their program
              </div>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}


/* ================================================================
   Training Now — live strip of athletes currently mid-session.
   Polls every 25s; self-hides when nobody is training.
   ================================================================ */
function elapsedSince(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "just now";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m`;
}

function TrainingNowStrip() {
  const [items, setItems] = useState([]);
  const [, setTick] = useState(0); // re-render so elapsed times stay fresh

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/coach/dashboard/training-now");
      setItems(data.items || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, 25000);
    const clock = setInterval(() => setTick((t) => t + 1), 30000);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, [load]);

  if (!items.length) return null;

  const initials = (name) => (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="mb-8 border border-green-500/30 bg-green-500/[0.04] p-4" data-testid="training-now-strip">
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-70 animate-ping" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
        </span>
        <span className="label-tiny text-green-500">TRAINING NOW ({items.length})</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="training-now-list">
        {items.map((it) => (
          <Link
            key={`${it.assignment_id}-${it.week}-${it.day}-${it.client_id}`}
            to={`/coach/clients/${it.client_id}`}
            className="shrink-0 flex items-center gap-2.5 border border-green-500/25 hover:border-green-500/60 bg-brand-primary/40 px-3 py-2 transition-colors"
            data-testid={`training-now-${it.client_id}`}
          >
            <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-[10px] font-bold shrink-0 overflow-hidden">
              {it.client_picture
                ? <img src={it.client_picture} alt="" className="w-full h-full object-cover" />
                : initials(it.client_name)}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-heading truncate max-w-[160px]">{it.client_name}</div>
              <div className="text-[11px] text-text-secondary truncate max-w-[160px]">
                {it.session_name} · <span className="text-green-500 tabular-nums">{elapsedSince(it.started_at)}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}



/* ================================================================
   MODULE 5 — Coach Dashboard Widgets
   ================================================================ */
function WidgetsSection({ widgets, alerts }) {
  const { today_sessions = [], recent_prs = [], completions_today = [], upcoming_competitions = [],
    adherence_7d = { completed: 0, due: 0, pct: null }, unread_messages = 0, upcoming_assessments = [],
    timezone: coachTz = "UTC" } = widgets || {};
  const alertItems = alerts?.items || [];
  const alertCounts = alerts?.counts || { readiness: 0, pain: 0, missed_session: 0 };
  const initials = (name) => (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const timeShort = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };

  return (
    <div className="mb-8" data-testid="coach-widgets">
      <div className="mb-4">
        <div className="label-tiny text-brand-accent mb-1">// TODAY&apos;S SIGNAL</div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="font-heading text-2xl">HUB</h2>
          <span className="label-tiny text-[10px] text-text-secondary" data-testid="hub-timezone">
            TIMES IN {String(coachTz).replace(/_/g, " ")}
          </span>
        </div>
      </div>

      {/* Workout adherence — last 7 days (completed due / due) */}
      <div className="border border-slate-200 p-4 mb-4 flex flex-wrap items-center justify-between gap-4" data-testid="widget-adherence">
        <div className="flex items-center gap-3">
          <ChartLine size={16} weight="duotone" className="text-brand-accent" />
          <div>
            <div className="label-tiny">WORKOUT ADHERENCE · LAST 7 DAYS</div>
            <div className="text-[11px] text-text-secondary mt-0.5" data-testid="adherence-detail">
              {adherence_7d.due === 0
                ? "No workouts due"
                : `${adherence_7d.completed} of ${adherence_7d.due} due workouts completed`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className={`font-heading text-3xl tabular-nums ${adherence_7d.pct == null ? "text-text-secondary" : adherence_7d.pct >= 80 ? "text-green-500" : adherence_7d.pct >= 50 ? "text-amber-400" : "text-red-400"}`} data-testid="adherence-pct">
            {adherence_7d.pct == null ? "—" : `${adherence_7d.pct}%`}
          </div>
          <Link to="/coach/clients" className="text-[10px] label-tiny text-brand-accent border border-brand-accent/40 px-2 py-1 hover:bg-brand-accent hover:text-surface-base transition" data-testid="adherence-history-link">
            HISTORY
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Today's sessions */}
        <div className="border border-slate-200 border-t-[3px] border-t-blue-500 bg-gradient-to-b from-blue-50/50 to-white p-4" data-testid="widget-today-sessions">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarBlank size={14} weight="duotone" className="text-brand-accent" />
              <span className="label-tiny">TODAY&apos;S SESSIONS ({today_sessions.length})</span>
            </div>
            {today_sessions.length > 0 && (
              <span className="label-tiny text-[10px] text-text-secondary tabular-nums">
                {today_sessions.filter((s) => s.status === "done").length}/{today_sessions.length} DONE
              </span>
            )}
          </div>
          {today_sessions.length === 0 ? (
            <div className="text-xs text-text-secondary py-6 text-center border border-dashed border-slate-200">
              No sessions scheduled today.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="widget-today-list">
              {today_sessions.map((s) => (
                <div
                  key={`${s.assignment_id}-${s.week}-${s.day}-${s.client_id}`}
                  className={`border ${s.status === "done" ? "border-green-500/30 bg-green-500/[0.03]" : s.status === "in_progress" ? "border-amber-400/40 bg-amber-400/[0.04]" : "border-slate-200"} px-3 py-2 flex items-center gap-3`}
                  data-testid={`today-session-${s.client_id}-${s.week}-${s.day}`}
                >
                  <div className="w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                    {initials(s.client_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/coach/clients/${s.client_id}`}
                      className="text-sm font-heading hover:text-brand-accent transition-colors block truncate"
                    >
                      {s.client_name}
                    </Link>
                    <div className="text-[11px] text-text-secondary truncate">
                      W{s.week}·D{s.day} · {s.session_name}
                      {s.session_type ? <span className="ml-1 uppercase">· {s.session_type}</span> : null}
                    </div>
                  </div>
                  {s.status === "done" ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] label-tiny text-green-500 border border-green-500/40 px-1.5 py-0.5"
                      data-testid={`today-session-status-${s.client_id}-${s.week}-${s.day}`}
                    >
                      <CheckCircle size={9} weight="fill" /> DONE {timeShort(s.completed_at)}
                    </span>
                  ) : s.status === "in_progress" ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] label-tiny text-amber-500 border border-amber-400/50 px-1.5 py-0.5"
                      data-testid={`today-session-status-${s.client_id}-${s.week}-${s.day}`}
                    >
                      <Play size={9} weight="fill" /> IN PROGRESS
                    </span>
                  ) : (
                    <span
                      className="text-[10px] label-tiny text-brand-accent border border-brand-accent/40 px-1.5 py-0.5"
                      data-testid={`today-session-status-${s.client_id}-${s.week}-${s.day}`}
                    >
                      SCHEDULED
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Module 10 — Unified Coach Alerts (readiness + pain + missed sessions) */}
        <div className="border border-slate-200 border-t-[3px] border-t-orange-500 bg-gradient-to-b from-orange-50/50 to-white p-4" data-testid="widget-coach-alerts">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Warning size={14} weight="duotone" className="text-red-400" />
              <span className="label-tiny">COACH ALERTS ({alertItems.length})</span>
            </div>
            <div className="flex items-center gap-2 text-[9px] label-tiny text-text-secondary">
              <span data-testid="alert-count-readiness">READINESS {alertCounts.readiness}</span>
              <span>·</span>
              <span data-testid="alert-count-pain">PAIN {alertCounts.pain}</span>
              <span>·</span>
              <span data-testid="alert-count-missed">MISSED {alertCounts.missed_session}</span>
            </div>
          </div>
          {alertItems.length === 0 ? (
            <div className="text-xs text-text-secondary py-6 text-center border border-dashed border-slate-200" data-testid="widget-alerts-empty">
              Roster is clear. No red flags in the last 7 days.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="widget-alerts-list">
              {alertItems.map((a, i) => {
                const kindStyle =
                  a.kind === "readiness"      ? { icon: Heartbeat, iconCls: "text-red-400" }        :
                  a.kind === "pain"           ? { icon: Bandaids,  iconCls: "text-orange-400" }     :
                                                { icon: CalendarBlank, iconCls: "text-amber-400" };
                const levelCls =
                  a.level === "high" ? "border-red-500/40 bg-red-500/[0.04]"     :
                  a.level === "med"  ? "border-amber-400/30 bg-amber-400/[0.04]" :
                                       "border-slate-200";
                const Icon = kindStyle.icon;
                return (
                  <Link
                    key={`${a.kind}-${a.client_id}-${a.date}-${i}`}
                    to={`/coach/clients/${a.client_id}`}
                    className={`block border ${levelCls} px-3 py-2 hover:bg-slate-100 transition`}
                    data-testid={`alert-row-${a.kind}-${a.client_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                        {initials(a.client_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-heading truncate flex items-center gap-1.5">
                          <Icon size={11} weight="duotone" className={kindStyle.iconCls} />
                          {a.client_name}
                          <span className="label-tiny text-[8px] px-1 py-0.5 bg-slate-100 text-slate-500 uppercase">{a.kind.replace("_", " ")}</span>
                        </div>
                        <div className="text-[11px] text-text-secondary truncate">{a.message}</div>
                      </div>
                      <div className="text-[9px] text-text-secondary tabular-nums text-right shrink-0">
                        {a.date}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent PRs */}
        <div className="border border-slate-200 border-t-[3px] border-t-amber-500 bg-gradient-to-b from-amber-50/50 to-white p-4" data-testid="widget-recent-prs">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Medal size={14} weight="duotone" className="text-yellow-400" />
              <span className="label-tiny">RECENT PRS 7D ({recent_prs.length})</span>
            </div>
          </div>
          {recent_prs.length === 0 ? (
            <div className="text-xs text-text-secondary py-6 text-center border border-dashed border-slate-200">
              No new PRs this week.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="widget-pr-list">
              {recent_prs.map((p) => (
                <Link
                  key={p.pr_id}
                  to={`/coach/clients/${p.client_id}`}
                  className="block border border-yellow-400/30 bg-yellow-400/[0.03] px-3 py-2 hover:bg-yellow-400/[0.08] transition"
                  data-testid={`pr-row-${p.pr_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                      {initials(p.client_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-heading truncate">
                        {p.client_name} <span className="text-text-secondary text-xs">· {p.exercise}</span>
                      </div>
                      <div className="text-[11px] text-text-secondary tabular-nums">
                        {new Date(p.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </div>
                    </div>
                    <div className="font-heading text-lg text-yellow-400 tabular-nums">
                      {p.value}<span className="text-[10px] text-text-secondary ml-1">{p.unit}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Completions today */}
        <div className="border border-slate-200 border-t-[3px] border-t-emerald-500 bg-gradient-to-b from-emerald-50/50 to-white p-4" data-testid="widget-completions-today">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckCircle size={14} weight="duotone" className="text-green-500" />
              <span className="label-tiny">LOGGED TODAY ({completions_today.length})</span>
            </div>
          </div>
          {completions_today.length === 0 ? (
            <div className="text-xs text-text-secondary py-6 text-center border border-dashed border-slate-200">
              No sessions completed yet today.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="widget-completions-list">
              {completions_today.map((c) => (
                <Link
                  key={c.completion_id}
                  to={`/coach/clients/${c.client_id}`}
                  className="block border border-green-500/30 bg-green-500/[0.03] px-3 py-2 hover:bg-green-500/[0.08] transition"
                  data-testid={`completion-row-${c.completion_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                      {initials(c.client_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-heading truncate">{c.client_name}</div>
                      <div className="text-[11px] text-text-secondary truncate">
                        W{c.week}·D{c.day} · {c.session_name} · {c.duration_min || 0} min
                      </div>
                    </div>
                    <div className="text-right">
                      {c.new_prs > 0 && (
                        <div className="text-[10px] label-tiny text-yellow-400 tabular-nums">
                          <Medal size={9} weight="fill" className="inline mr-0.5" /> {c.new_prs} PR{c.new_prs > 1 ? "S" : ""}
                        </div>
                      )}
                      <div className="text-[10px] text-text-secondary tabular-nums">{timeShort(c.completed_at)}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* MODULE 7 — Upcoming Competitions countdown */}
        <div className="border border-slate-200 border-t-[3px] border-t-rose-500 bg-gradient-to-b from-rose-50/50 to-white p-4" data-testid="widget-upcoming-competitions">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy size={14} weight="duotone" className="text-brand-accent" />
              <span className="label-tiny">UPCOMING COMPETITIONS ({upcoming_competitions.length})</span>
            </div>
            {upcoming_competitions.length > 0 && (
              <span className="label-tiny text-[10px] text-text-secondary">NEXT 60D</span>
            )}
          </div>
          {upcoming_competitions.length === 0 ? (
            <div className="text-xs text-text-secondary py-6 text-center border border-dashed border-slate-200" data-testid="widget-competitions-empty">
              No competitions in the next 60 days.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="widget-competitions-list">
              {upcoming_competitions.map((c) => {
                const dstyle =
                  c.days_until == null ? "bg-slate-100 text-slate-600 border border-slate-200" :
                  c.days_until <= 7  ? "bg-red-100 text-red-800 border border-red-300"      :
                  c.days_until <= 30 ? "bg-amber-100 text-amber-900 border border-amber-300"  :
                                       "bg-blue-100 text-blue-800 border border-blue-300";
                const pstyle =
                  c.priority === "A" ? "bg-red-100 text-red-800 border border-red-300"          :
                  c.priority === "B" ? "bg-amber-100 text-amber-900 border border-amber-300" :
                  c.priority === "C" ? "bg-slate-100 text-slate-700 border border-slate-300"     : null;
                return (
                  <Link
                    key={c.competition_id}
                    to={`/coach/clients/${c.client_id}#competitions`}
                    className="block border border-slate-200 hover:border-brand-accent/40 hover:bg-slate-50 px-3 py-2 transition"
                    data-testid={`competition-row-${c.competition_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                        {(c.client_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-heading truncate">{c.client_name}</span>
                          {pstyle && (
                            <span className={`label-tiny text-[8px] px-1 py-0.5 ${pstyle}`} data-testid={`competition-priority-${c.competition_id}`}>
                              {c.priority}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-text-secondary truncate">
                          {c.name}{c.event ? ` · ${c.event}` : ""}
                        </div>
                      </div>
                      <span className={`text-[10px] label-tiny px-1.5 py-0.5 tabular-nums ${dstyle}`} data-testid={`competition-countdown-${c.competition_id}`}>
                        {c.days_until === 0 ? "TODAY" : c.days_until === 1 ? "1D" : `${c.days_until}D`}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Unread athlete messages */}
        <div className="border border-slate-200 border-t-[3px] border-t-violet-500 bg-gradient-to-b from-violet-50/50 to-white p-4" data-testid="widget-unread-messages">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ChatCircleDots size={14} weight="duotone" className="text-brand-accent" />
              <span className="label-tiny">UNREAD MESSAGES ({unread_messages})</span>
            </div>
          </div>
          {unread_messages === 0 ? (
            <div className="text-xs text-text-secondary py-6 text-center border border-dashed border-slate-200" data-testid="widget-messages-empty">
              No unread messages.
            </div>
          ) : (
            <Link to="/messages" className="block border border-brand-accent/40 bg-brand-accent/[0.05] px-3 py-3 hover:bg-brand-accent/[0.12] transition" data-testid="unread-messages-link">
              <div className="flex items-center gap-3">
                <div className="font-heading text-2xl text-brand-accent tabular-nums">{unread_messages}</div>
                <div className="text-sm text-text-secondary">unread message{unread_messages > 1 ? "s" : ""} from your athletes — open inbox</div>
              </div>
            </Link>
          )}
        </div>

        {/* Upcoming assessments */}
        <div className="border border-slate-200 border-t-[3px] border-t-cyan-500 bg-gradient-to-b from-cyan-50/50 to-white p-4" data-testid="widget-upcoming-assessments">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ClipboardText size={14} weight="duotone" className="text-brand-accent" />
              <span className="label-tiny">UPCOMING ASSESSMENTS ({upcoming_assessments.length})</span>
            </div>
            {upcoming_assessments.length > 0 && <span className="label-tiny text-[10px] text-text-secondary">NEXT 60D</span>}
          </div>
          {upcoming_assessments.length === 0 ? (
            <div className="text-xs text-text-secondary py-6 text-center border border-dashed border-slate-200" data-testid="widget-assessments-empty">
              No assessments scheduled.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="widget-assessments-list">
              {upcoming_assessments.map((a) => (
                <Link
                  key={`${a.client_id}-${a.date}`}
                  to={`/coach/clients/${a.client_id}/assessment`}
                  className="block border border-slate-200 hover:border-brand-accent/40 hover:bg-slate-50 px-3 py-2 transition"
                  data-testid={`assessment-row-${a.client_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                      {initials(a.client_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-heading truncate">{a.client_name}</div>
                      <div className="text-[11px] text-text-secondary tabular-nums">
                        {a.date ? new Date(a.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
                      </div>
                    </div>
                    {a.days_until != null && (
                      <span className="text-[10px] label-tiny px-1.5 py-0.5 tabular-nums bg-blue-100 text-blue-800 border border-blue-300">
                        {a.days_until === 0 ? "TODAY" : `${a.days_until}D`}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── STRENGTH COACH — TAB COMPONENTS ────────────────────────────────────────

const LIFT_KEYS = ["squat", "bench", "deadlift", "ohp"];

const OneRepMaxBoard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [inferOn, setInferOn] = useState(true);

  useEffect(() => {
    setBusy(true);
    api.get(`/coach/one-rep-max-board?include_inferred=${inferOn}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load 1RM board"))
      .finally(() => setBusy(false));
  }, [inferOn]);

  if (busy) return <div className="label-tiny text-text-secondary">Loading 1RM board...</div>;
  if (!data || data.clients.length === 0) {
    return <div className="p-10 text-center text-text-secondary text-sm">No clients yet.</div>;
  }

  const cellColor = (cell) => {
    if (!cell) return "text-text-secondary/40";
    if (cell.delta_pct != null && cell.delta_pct >= 2.5) return "text-green-400";
    if (cell.delta_pct != null && cell.delta_pct <= -5) return "text-red-400";
    return "text-brand-accent";
  };

  return (
    <div className="space-y-4" data-testid="coach-1rm-board">
      <div className="flex items-center gap-4">
        <div className="label-tiny text-[10px] text-text-secondary">// BIG-4 · BEST 1RM PER CLIENT</div>
        <label className="inline-flex items-center gap-2 text-xs text-text-secondary ml-auto">
          <input
            type="checkbox"
            checked={inferOn}
            onChange={(e) => setInferOn(e.target.checked)}
            data-testid="1rm-infer-toggle"
          />
          INFER FROM TOP SETS (EPLEY)
        </label>
      </div>
      <div className="border border-white/10 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left px-3 py-2 label-tiny text-[10px] text-text-secondary tracking-widest">CLIENT</th>
              {data.lifts.map((l) => (
                <th key={l.key} className="text-right px-3 py-2 label-tiny text-[10px] text-text-secondary tracking-widest">{l.label.toUpperCase()}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.clients.map((c) => (
              <tr key={c.client_id} data-testid={`1rm-row-${c.client_id}`}>
                <td className="px-3 py-2">
                  <div className="text-sm">{c.name}</div>
                  <div className="text-[10px] text-text-secondary truncate">{c.email}</div>
                </td>
                {LIFT_KEYS.map((k) => {
                  const cell = c.cells[k];
                  return (
                    <td key={k} className="px-3 py-2 text-right tabular-nums" data-testid={`1rm-cell-${c.client_id}-${k}`}>
                      {cell ? (
                        <>
                          <div className={`text-sm font-heading ${cellColor(cell)}`}>{cell.value} <span className="text-[10px] text-text-secondary">{cell.unit}</span></div>
                          <div className="text-[10px] text-text-secondary">
                            {cell.source === "inferred" ? "est." : ""}
                            {cell.date && ` ${cell.date}`}
                          </div>
                          {cell.delta_pct != null && (
                            <div className={`text-[10px] ${cell.delta_pct >= 0 ? "text-green-500" : "text-red-500"}`}>
                              {cell.delta_pct >= 0 ? "▲" : "▼"} {Math.abs(cell.delta_pct)}%
                            </div>
                          )}
                        </>
                      ) : <span className="text-text-secondary/40">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-text-secondary">
        Green ≥ +2.5% vs prior best · Red ≤ −5% · est. = inferred from a top set via Epley (w × (1+r/30)).
      </p>
    </div>
  );
};

const LoadCalculatorTab = () => {
  const [oneRM, setOneRM] = useState(150);
  const [reps, setReps] = useState(5);
  const [mode, setMode] = useState("rpe");
  const [rpe, setRPE] = useState(8);
  const [pct, setPct] = useState(80);
  const [roundTo, setRoundTo] = useState(2.5);
  const [result, setResult] = useState(null);
  const [chart, setChart] = useState(null);

  useEffect(() => {
    api.get("/coach/rpe-chart").then(({ data }) => setChart(data)).catch(() => {});
  }, []);

  const compute = async () => {
    try {
      const { data } = await api.post("/coach/load-calc", {
        one_rm: parseFloat(oneRM),
        reps: parseInt(reps, 10),
        rpe: mode === "rpe" ? parseFloat(rpe) : null,
        percent: mode === "pct" ? parseFloat(pct) : null,
        round_to: parseFloat(roundTo),
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calc failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="coach-load-calc">
      <div className="border border-white/10 p-4 space-y-3">
        <div className="label-tiny text-[10px] text-brand-accent">// LOAD CALCULATOR</div>
        <div className="grid md:grid-cols-4 gap-2">
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">1RM</label>
            <Input type="number" value={oneRM} onChange={(e) => setOneRM(e.target.value)} data-testid="calc-1rm" />
          </div>
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">REPS (1-10)</label>
            <Input type="number" min="1" max="10" value={reps} onChange={(e) => setReps(e.target.value)} data-testid="calc-reps" />
          </div>
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">MODE</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="calc-mode">
              <option value="rpe">RPE-based (Zourdos)</option>
              <option value="pct">Percent of 1RM</option>
            </select>
          </div>
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">ROUND TO</label>
            <Input type="number" step="0.5" value={roundTo} onChange={(e) => setRoundTo(e.target.value)} data-testid="calc-round" />
          </div>
        </div>
        {mode === "rpe" ? (
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">TARGET RPE (6-10)</label>
            <Input type="number" step="0.5" min="6" max="10" value={rpe} onChange={(e) => setRPE(e.target.value)} data-testid="calc-rpe" />
          </div>
        ) : (
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">PERCENT (0-100)</label>
            <Input type="number" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="calc-pct" />
          </div>
        )}
        <button onClick={compute} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest" data-testid="calc-run">COMPUTE</button>
        {result && (
          <div className="border border-heat/40 bg-heat/5 p-4" data-testid="calc-result">
            <div className="text-3xl font-heading text-heat tabular-nums">{result.rounded_load}</div>
            <div className="text-[11px] text-text-secondary mt-1">
              ≈ {result.percent}% of {result.one_rm} × {result.reps} reps
              {result.rpe && ` @ RPE ${result.rpe}`}
              {" "}(raw {result.raw_load}, rounded to {result.round_to}).
            </div>
          </div>
        )}
      </div>

      {chart && (
        <div className="border border-white/10 overflow-auto" data-testid="rpe-chart">
          <div className="p-3 label-tiny text-[10px] text-brand-accent bg-white/5">// ZOURDOS RPE ↔ %1RM CHART</div>
          <table className="w-full text-[11px] tabular-nums">
            <thead>
              <tr>
                <th className="text-left px-2 py-1 label-tiny text-[9px] text-text-secondary">REPS</th>
                {chart.rpes.map((r) => (
                  <th key={r} className="text-right px-2 py-1 label-tiny text-[9px] text-text-secondary">RPE {r}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {chart.reps.map((r) => (
                <tr key={r}>
                  <td className="text-left px-2 py-1 text-brand-accent">{r}</td>
                  {chart.rpes.map((rp) => (
                    <td key={rp} className="text-right px-2 py-1">{chart.chart[r][rp]}%</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AutoregTab = ({ clients }) => {
  const [clientId, setClientId] = useState(clients?.[0]?.client?.user_id || "");
  const [lift, setLift] = useState("squat");
  const [targetRpe, setTargetRpe] = useState(8);
  const [reps, setReps] = useState(5);
  const [roundTo, setRoundTo] = useState(2.5);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!clientId) { toast.error("Select a client"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/coach/clients/${clientId}/autoreg`, {
        lift,
        target_rpe: parseFloat(targetRpe),
        reps: parseInt(reps, 10),
        round_to: parseFloat(roundTo),
      });
      setResult(data);
    } catch (e) {
      setResult(null);
      toast.error(e?.response?.data?.detail || "Autoreg failed");
    } finally { setBusy(false); }
  };

  const stateColor = (s) => ({
    high: "text-green-500", moderate: "text-brand-accent", low: "text-red-500", unknown: "text-text-secondary",
  }[s] || "text-text-secondary");

  return (
    <div className="space-y-4" data-testid="coach-autoreg">
      <div className="border border-white/10 p-4 space-y-3">
        <div className="label-tiny text-[10px] text-brand-accent">// TODAY&apos;S SUGGESTED LOAD (RPE-first, readiness-modified)</div>
        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">CLIENT</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="autoreg-client">
              <option value="">Select client...</option>
              {clients.map((c) => (<option key={c.client?.user_id} value={c.client?.user_id}>{c.client?.name || c.client?.email}</option>))}
            </select>
          </div>
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">LIFT</label>
            <select value={lift} onChange={(e) => setLift(e.target.value)} className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="autoreg-lift">
              <option value="squat">Back Squat</option>
              <option value="bench">Bench Press</option>
              <option value="deadlift">Deadlift</option>
              <option value="ohp">Overhead Press</option>
            </select>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-2">
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">TARGET RPE</label>
            <Input type="number" step="0.5" min="6" max="10" value={targetRpe} onChange={(e) => setTargetRpe(e.target.value)} data-testid="autoreg-rpe" />
          </div>
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">REPS</label>
            <Input type="number" min="1" max="10" value={reps} onChange={(e) => setReps(e.target.value)} data-testid="autoreg-reps" />
          </div>
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">ROUND TO</label>
            <Input type="number" step="0.5" value={roundTo} onChange={(e) => setRoundTo(e.target.value)} data-testid="autoreg-round" />
          </div>
        </div>
        <button onClick={run} disabled={busy} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50" data-testid="autoreg-run">
          {busy ? "COMPUTING..." : "SUGGEST TODAY'S LOAD"}
        </button>
      </div>

      {result && (
        <div className="border border-heat/40 bg-heat/5 p-5 space-y-3" data-testid="autoreg-result">
          <div className="flex items-baseline gap-3">
            <div className="text-4xl font-heading text-heat tabular-nums">{result.suggested_load}</div>
            <div className="text-[11px] text-text-secondary">
              @ {result.percent}% × {result.reps} · adjusted RPE {result.adjusted_rpe}
            </div>
          </div>
          <div className="text-sm text-text-secondary">{result.note}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-white/10">
            <div>
              <div className="label-tiny text-[10px] text-text-secondary">1RM</div>
              <div className="text-sm">{result.one_rm} {result.one_rm_unit}</div>
              <div className="text-[10px] text-text-secondary">{result.one_rm_source.replace(/_/g, " ")}</div>
            </div>
            <div>
              <div className="label-tiny text-[10px] text-text-secondary">READINESS</div>
              <div className={`text-sm font-heading ${stateColor(result.readiness_state)}`}>{result.readiness_score ?? "—"}</div>
              <div className="text-[10px] text-text-secondary">{result.readiness_state}</div>
            </div>
            <div>
              <div className="label-tiny text-[10px] text-text-secondary">PLANNED RPE</div>
              <div className="text-sm">{result.planned_rpe}</div>
            </div>
            <div>
              <div className="label-tiny text-[10px] text-text-secondary">ADJUSTMENT</div>
              <div className={`text-sm font-heading ${result.rpe_adjust >= 0 ? "text-green-500" : "text-red-500"}`}>
                {result.rpe_adjust >= 0 ? "+" : ""}{result.rpe_adjust}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ─── STRENGTH COACH — BATCH 2 TAB COMPONENTS ────────────────────────────────

const VolumeReportTab = ({ clients }) => {
  const [clientId, setClientId] = useState(clients?.[0]?.client?.user_id || "");
  const [weeks, setWeeks] = useState(4);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    setBusy(true);
    api.get(`/coach/clients/${clientId}/volume-report?weeks=${weeks}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load volume report"))
      .finally(() => setBusy(false));
  }, [clientId, weeks]);

  return (
    <div className="space-y-4" data-testid="coach-volume-report">
      <div className="border border-white/10 p-4 grid md:grid-cols-2 gap-3">
        <div>
          <label className="label-tiny text-[10px] text-text-secondary">CLIENT</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="volume-client">
            <option value="">Select client...</option>
            {clients.map((c) => (<option key={c.client?.user_id} value={c.client?.user_id}>{c.client?.name || c.client?.email}</option>))}
          </select>
        </div>
        <div>
          <label className="label-tiny text-[10px] text-text-secondary">WEEKS BACK</label>
          <Input type="number" min="1" max="26" value={weeks} onChange={(e) => setWeeks(parseInt(e.target.value, 10) || 4)} data-testid="volume-weeks" />
        </div>
      </div>

      {busy && <div className="label-tiny text-text-secondary">Loading volume report...</div>}
      {!busy && !clientId && <div className="p-10 text-center text-text-secondary text-sm">Pick a client to see their volume rollup.</div>}
      {!busy && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border border-white/10 p-3">
              <div className="text-2xl font-heading text-brand-accent tabular-nums">{data.totals.workouts}</div>
              <div className="label-tiny text-[10px] text-text-secondary mt-1">WORKOUTS</div>
            </div>
            <div className="border border-white/10 p-3">
              <div className="text-2xl font-heading text-brand-accent tabular-nums">{data.totals.sets.toLocaleString()}</div>
              <div className="label-tiny text-[10px] text-text-secondary mt-1">TOTAL SETS</div>
            </div>
            <div className="border border-white/10 p-3">
              <div className="text-2xl font-heading text-brand-accent tabular-nums">{data.totals.reps.toLocaleString()}</div>
              <div className="label-tiny text-[10px] text-text-secondary mt-1">TOTAL REPS</div>
            </div>
            <div className="border border-white/10 p-3">
              <div className="text-2xl font-heading text-heat tabular-nums">{data.totals.tonnage_kg.toLocaleString()}<span className="text-sm text-text-secondary ml-1">kg</span></div>
              <div className="label-tiny text-[10px] text-text-secondary mt-1">TONNAGE</div>
            </div>
          </div>
          {data.weeks.length === 0 ? (
            <div className="p-10 text-center text-text-secondary text-sm">No workouts logged in this window.</div>
          ) : (
            <div className="border border-white/10 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-3 py-2 label-tiny text-[10px] text-text-secondary tracking-widest">WEEK</th>
                    <th className="text-right px-3 py-2 label-tiny text-[10px] text-text-secondary tracking-widest">WORKOUTS</th>
                    <th className="text-right px-3 py-2 label-tiny text-[10px] text-text-secondary tracking-widest">SETS</th>
                    <th className="text-right px-3 py-2 label-tiny text-[10px] text-text-secondary tracking-widest">TONNAGE (kg)</th>
                    <th className="text-right px-3 py-2 label-tiny text-[10px] text-text-secondary tracking-widest">Δ vs prior</th>
                    {data.muscles.map((m) => (
                      <th key={m} className="text-right px-3 py-2 label-tiny text-[10px] text-text-secondary tracking-widest">{m.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data.weeks.map((w) => (
                    <tr key={w.week} data-testid={`volume-week-${w.week}`}>
                      <td className="px-3 py-2 font-mono">{w.week}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{w.workouts}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{w.total_sets}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-heading text-brand-accent">{w.total_tonnage_kg.toLocaleString()}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${w.delta_pct == null ? "text-text-secondary/40" : w.delta_pct >= 0 ? "text-green-500" : "text-red-500"}`}>
                        {w.delta_pct == null ? "—" : `${w.delta_pct >= 0 ? "▲" : "▼"} ${Math.abs(w.delta_pct)}%`}
                        {w.fatigue_flag && <span className="ml-1 text-heat" title="Fatigue signal">⚠</span>}
                      </td>
                      {data.muscles.map((m) => {
                        const cell = w.muscles[m];
                        return (
                          <td key={m} className="px-3 py-2 text-right tabular-nums text-text-secondary">
                            {cell ? cell.tonnage_kg.toLocaleString() : <span className="opacity-30">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[10px] text-text-secondary">
            ⚠ = weekly tonnage jumped ≥30% vs prior week (fatigue signal).
          </p>
        </>
      )}
    </div>
  );
};

const TemplatesTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [form, setForm] = useState({ name: "", category: "strength", session_type: "strength", tags: "", exercises_raw: "" });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/coach/session-templates");
      setRows(data.items || []);
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const parseExercises = (raw) => {
    // Each line: "Name | sets | reps | weight | rpe" (weight+rpe optional)
    return raw.split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
      const parts = line.split("|").map((p) => p.trim());
      const [name, sets, reps, weight, rpe] = parts;
      const ex = { name };
      if (sets) ex.sets = parseInt(sets, 10);
      if (reps) ex.reps = parseInt(reps, 10);
      if (weight) ex.weight_kg = parseFloat(weight);
      if (rpe) ex.rpe = parseFloat(rpe);
      return ex;
    });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const body = {
      name: form.name,
      category: form.category,
      session_type: form.session_type,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      exercises: parseExercises(form.exercises_raw),
    };
    try {
      if (editingId) {
        await api.patch(`/coach/session-templates/${editingId}`, body);
        toast.success("Template updated");
      } else {
        await api.post("/coach/session-templates", body);
        toast.success("Template created");
      }
      setForm({ name: "", category: "strength", session_type: "strength", tags: "", exercises_raw: "" });
      setEditingId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const edit = (t) => {
    setEditingId(t.template_id);
    setForm({
      name: t.name,
      category: t.category || "strength",
      session_type: t.session_type || "strength",
      tags: (t.tags || []).join(", "),
      exercises_raw: (t.exercises || []).map((e) => [e.name, e.sets, e.reps, e.weight_kg, e.rpe].filter((v) => v !== undefined && v !== null && v !== "").join(" | ")).join("\n"),
    });
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await api.delete(`/coach/session-templates/${t.template_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="coach-templates">
      <div className="border border-white/10 p-4 space-y-2">
        <div className="label-tiny text-[10px] text-brand-accent">// {editingId ? "EDIT" : "NEW"} TEMPLATE</div>
        <div className="grid md:grid-cols-3 gap-2">
          <Input placeholder="Name (e.g. Squat Day)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tpl-name" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="tpl-category">
            <option value="strength">Strength</option>
            <option value="power">Power</option>
            <option value="conditioning">Conditioning</option>
            <option value="mobility">Mobility</option>
            <option value="other">Other</option>
          </select>
          <Input placeholder="Tags (comma-separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="tpl-tags" />
        </div>
        <label className="label-tiny text-[10px] text-text-secondary">EXERCISES · one per line, format: NAME | SETS | REPS | WEIGHT | RPE</label>
        <textarea
          placeholder={"Back Squat | 5 | 5 | 100 | 8\nBench Press | 4 | 8 | 80 | 7"}
          rows={5}
          value={form.exercises_raw}
          onChange={(e) => setForm({ ...form, exercises_raw: e.target.value })}
          className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono"
          data-testid="tpl-exercises"
        />
        <div className="flex gap-2">
          <button onClick={save} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest" data-testid="tpl-save">
            {editingId ? "UPDATE TEMPLATE" : "CREATE TEMPLATE"}
          </button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setForm({ name: "", category: "strength", session_type: "strength", tags: "", exercises_raw: "" }); }} className="px-4 py-2 text-xs label-tiny tracking-widest border border-white/20 text-text-secondary" data-testid="tpl-cancel">
              CANCEL
            </button>
          )}
        </div>
      </div>

      {busy && <div className="label-tiny text-text-secondary">Loading...</div>}
      <div className="border border-white/10 divide-y divide-white/5">
        {rows.length === 0 && !busy && <div className="p-10 text-center text-text-secondary text-sm">No templates yet. Build one above.</div>}
        {rows.map((t) => (
          <div key={t.template_id} className="p-4 flex items-start gap-3" data-testid={`tpl-row-${t.template_id}`}>
            <div className="label-tiny text-[10px] tracking-widest px-2 py-1 bg-brand-accent/15 text-brand-accent shrink-0">
              {(t.category || "OTHER").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-base">{t.name}</div>
              <div className="text-[11px] text-text-secondary">
                {(t.exercises || []).length} exercise(s)
                {t.tags && t.tags.length > 0 && ` · ${t.tags.join(", ")}`}
              </div>
              <div className="text-[10px] text-text-secondary/70 mt-0.5 font-mono truncate">
                {(t.exercises || []).map((e) => e.name).join(", ") || "—"}
              </div>
            </div>
            <button onClick={() => edit(t)} className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-white/20 hover:!border-heat hover:!text-heat" data-testid={`tpl-edit-${t.template_id}`}>EDIT</button>
            <button onClick={() => remove(t)} className="p-1.5 text-text-secondary hover:!text-red-500" data-testid={`tpl-delete-${t.template_id}`}><Warning size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const PlannerTab = () => {
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState("");
  const [mode, setMode] = useState("deload");
  const [everyN, setEveryN] = useState(4);
  const [peakWeek, setPeakWeek] = useState(8);
  const [taperWeeks, setTaperWeeks] = useState(3);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/coach/programs")
      .then(({ data }) => setPrograms(data || []))
      .catch(() => toast.error("Could not load programs"));
  }, []);

  const run = async () => {
    if (!programId) { toast.error("Pick a program"); return; }
    setBusy(true);
    try {
      const url = mode === "deload"
        ? `/coach/programs/${programId}/plan-deload`
        : `/coach/programs/${programId}/plan-peaking`;
      const body = mode === "deload"
        ? { every_n_weeks: parseInt(everyN, 10) || 4 }
        : { peak_week: parseInt(peakWeek, 10), taper_weeks: parseInt(taperWeeks, 10) || 3 };
      const { data } = await api.post(url, body);
      setResult(data);
      toast.success(mode === "deload"
        ? `Deload injected · ${data.days_updated} day(s) updated`
        : `Peak plan applied · ${data.days_updated} day(s) updated`);
    } catch (e) {
      setResult(null);
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const selected = programs.find((p) => p.program_id === programId);

  return (
    <div className="space-y-4" data-testid="coach-planner">
      <div className="border border-white/10 p-4 space-y-3">
        <div className="label-tiny text-[10px] text-brand-accent">// DELOAD / PEAKING PLANNER</div>
        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">PROGRAM</label>
            <select value={programId} onChange={(e) => setProgramId(e.target.value)} className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="planner-program">
              <option value="">Select program...</option>
              {programs.map((p) => (<option key={p.program_id} value={p.program_id}>{p.name} ({p.weeks}wk)</option>))}
            </select>
          </div>
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">MODE</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="planner-mode">
              <option value="deload">Deload (auto-inject rest weeks)</option>
              <option value="peaking">Peaking (taper into meet week)</option>
            </select>
          </div>
        </div>
        {mode === "deload" ? (
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">DELOAD EVERY N WEEKS</label>
            <Input type="number" min="2" max="12" value={everyN} onChange={(e) => setEveryN(e.target.value)} data-testid="planner-every-n" />
            <p className="text-[10px] text-text-secondary mt-1">
              Deload = sets ×0.5, intensity ×0.8, RPE −1.5. Session labelled DELOAD.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-2">
            <div>
              <label className="label-tiny text-[10px] text-text-secondary">PEAK WEEK</label>
              <Input type="number" min="1" max={selected?.weeks || 12} value={peakWeek} onChange={(e) => setPeakWeek(e.target.value)} data-testid="planner-peak-week" />
            </div>
            <div>
              <label className="label-tiny text-[10px] text-text-secondary">TAPER LENGTH (WEEKS)</label>
              <Input type="number" min="1" max="6" value={taperWeeks} onChange={(e) => setTaperWeeks(e.target.value)} data-testid="planner-taper-weeks" />
            </div>
            <p className="md:col-span-2 text-[10px] text-text-secondary">
              Peak: volume drops linearly to ~40%; intensity climbs to ~95% at peak. Sessions labelled TAPER / PEAK.
            </p>
          </div>
        )}
        <button onClick={run} disabled={busy || !programId} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50" data-testid="planner-run">
          {busy ? "APPLYING..." : (mode === "deload" ? "INJECT DELOAD" : "APPLY PEAKING")}
        </button>
      </div>

      {result && (
        <div className="border border-heat/40 bg-heat/5 p-4" data-testid="planner-result">
          <div className="label-tiny text-[10px] text-heat mb-1">// APPLIED</div>
          {mode === "deload" ? (
            <div className="text-sm">
              Deload weeks: <span className="font-heading text-heat">{result.deload_weeks.join(", ")}</span>
              <span className="text-text-secondary"> · {result.days_updated} training day(s) updated.</span>
            </div>
          ) : (
            <div className="text-sm">
              Peak week <span className="font-heading text-heat">{result.peak_week}</span>,
              taper starts week <span className="font-heading text-heat">{result.taper_start_week}</span>
              <span className="text-text-secondary"> · {result.days_updated} training day(s) updated.</span>
            </div>
          )}
          <p className="text-[11px] text-text-secondary mt-2">
            Open the Program Builder to review the modified sessions.
          </p>
        </div>
      )}
    </div>
  );
};

